import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import useApiHook from "../hooks/useApiHook";
import { useNotification } from "../store/notification-context";
import { useAuth } from "../store/auth-context";
import { useUser } from "../store/user-context";
import AdminUserEditDialog from "../components/admin/AdminUserEditDialog";
import Body from "../components/layout/Body";
import { ANALYSIS_FUNCTIONS, EXTRA_FUNCTIONS } from "../components/ai/catalog";

const formatMinutes = (totalSeconds) => {
    const safeTotalSeconds = Math.max(0, Number(totalSeconds || 0));
    const hours = Math.floor(safeTotalSeconds / 3600);
    const minutes = Math.floor((safeTotalSeconds % 3600) / 60);
    const seconds = safeTotalSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatSubscription = (packageId) => packageId || "Free";

const formatRegistrationDate = (value) => {
    if (!value) {
        return "-";
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
        return "-";
    }

    return new Intl.DateTimeFormat("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
    }).format(parsedDate);
};

const mapPromptConfigToDraft = (promptConfig) => ({
    systemInstructionsText: promptConfig?.resolvedSystemInstructionsText || "",
    chunkInstructionsText: promptConfig?.resolvedChunkInstructionsText || "",
    aggregateInstructionsText: promptConfig?.resolvedAggregateInstructionsText || ""
});

const PROMPT_FUNCTION_GROUPS = [
    {
        key: "analysis",
        title: "Analisi Seduta",
        items: ANALYSIS_FUNCTIONS
    },
    {
        key: "extra",
        title: "Monitoraggio Percorso",
        items: EXTRA_FUNCTIONS
    }
];

const SUMMARY_PROMPT_VARIABLES = [
    { token: '{nome}', label: 'Nome' },
    { token: '{cognome}', label: 'Cognome' },
    { token: '{titolo}', label: 'Titolo professionale' },
    { token: '{professione}', label: 'Professione' },
    { token: '{orientamento}', label: 'Orientamento' },
    { token: '{approccio}', label: 'Approcci integrativi' }
];

const AdminDashboardPage = () => {
    const { makeRequest } = useApiHook();
    const { pushNotification } = useNotification();
    const { user: authenticatedUser } = useAuth();
    const userCtx = useUser();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isPromptDialogOpen, setIsPromptDialogOpen] = useState(false);
    const [promptConfigs, setPromptConfigs] = useState([]);
    const [promptDrafts, setPromptDrafts] = useState({});
    const [selectedPromptFunctionKey, setSelectedPromptFunctionKey] = useState("");
    const [isPromptLoading, setIsPromptLoading] = useState(false);
    const [isPromptSaving, setIsPromptSaving] = useState(false);
    const [promptErrorMessage, setPromptErrorMessage] = useState("");
    const [isProviderDialogOpen, setIsProviderDialogOpen] = useState(false);
    const [providerDraft, setProviderDraft] = useState({ openAIApiKey: "" });
    const [isProviderLoading, setIsProviderLoading] = useState(false);
    const [isProviderSaving, setIsProviderSaving] = useState(false);
    const [providerErrorMessage, setProviderErrorMessage] = useState("");
    const [showOpenAIApiKey, setShowOpenAIApiKey] = useState(false);

    useEffect(() => {
        const loadUsers = async () => {
            setIsLoading(true);
            setErrorMessage("");

            const result = await makeRequest('/user/admin/users');

            if (result?.state === 'success') {
                setUsers(Array.isArray(result.data) ? result.data : []);
            } else {
                setErrorMessage(result?.message || "Impossibile caricare gli utenti.");
            }

            setIsLoading(false);
        };

        loadUsers();
    }, [makeRequest]);

    const selectedUser = users.find((user) => user.id === selectedUserId) || null;
    const selectedPromptConfig = promptConfigs.find((item) => item.functionKey === selectedPromptFunctionKey) || null;
    const selectedPromptDraft = promptDrafts[selectedPromptFunctionKey] || mapPromptConfigToDraft(selectedPromptConfig);
    const promptConfigsByKey = promptConfigs.reduce((accumulator, promptConfig) => {
        accumulator[promptConfig.functionKey] = promptConfig;
        return accumulator;
    }, {});
    const groupedPromptConfigs = PROMPT_FUNCTION_GROUPS.map((group) => ({
        ...group,
        items: group.items
            .map((item) => promptConfigsByKey[item.key])
            .filter(Boolean)
    })).filter((group) => group.items.length > 0);

    const handleSelectRow = (userId) => {
        setSelectedUserId((currentSelectedUserId) => currentSelectedUserId === userId ? null : userId);
    };

    const handleOpenEditDialog = () => {
        if (!selectedUser) {
            return;
        }

        setIsEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        if (isSaving) {
            return;
        }

        setIsEditDialogOpen(false);
    };

    const handleSaveUser = async ({ userId, role, minutesToAdd }) => {
        setIsSaving(true);

        const result = await makeRequest('/user/admin/users/update', 'post', {
            userId,
            role,
            minutesToAdd
        });

        if (result?.state === 'success' && result.data) {
            setUsers((currentUsers) => currentUsers.map((user) => (
                user.id === result.data.id ? result.data : user
            )));
            if (Number(result.data.id) === Number(authenticatedUser?.id)) {
                await userCtx.fetchUsage();
            }
            setIsEditDialogOpen(false);
            pushNotification({
                title: "Utente aggiornato",
                message: "Ruolo e minuti sono stati salvati."
            });
        } else {
            pushNotification({
                severity: "error",
                title: "Aggiornamento fallito",
                message: result?.message || "Non siamo riusciti a salvare le modifiche."
            });
        }

        setIsSaving(false);
    };

    const loadPromptConfigs = async () => {
        setIsPromptLoading(true);
        setPromptErrorMessage("");

        const result = await makeRequest('/ai/admin/prompt-configs');

        if (result?.state === 'success') {
            const nextConfigs = Array.isArray(result.data) ? result.data : [];
            const nextDrafts = nextConfigs.reduce((accumulator, promptConfig) => {
                accumulator[promptConfig.functionKey] = mapPromptConfigToDraft(promptConfig);
                return accumulator;
            }, {});

            setPromptConfigs(nextConfigs);
            setPromptDrafts(nextDrafts);
            setSelectedPromptFunctionKey((currentValue) => currentValue || nextConfigs[0]?.functionKey || "");
        } else {
            setPromptErrorMessage(result?.message || "Impossibile caricare i prompt AI.");
        }

        setIsPromptLoading(false);
    };

    const handleOpenPromptDialog = async () => {
        setIsPromptDialogOpen(true);

        if (promptConfigs.length === 0) {
            await loadPromptConfigs();
        }
    };

    const handleClosePromptDialog = () => {
        if (isPromptSaving) {
            return;
        }

        setIsPromptDialogOpen(false);
    };

    const handlePromptDraftChange = (field, value) => {
        setPromptDrafts((currentDrafts) => ({
            ...currentDrafts,
            [selectedPromptFunctionKey]: {
                ...currentDrafts[selectedPromptFunctionKey],
                [field]: value
            }
        }));
    };

    const handleSavePromptConfig = async () => {
        if (!selectedPromptFunctionKey || !selectedPromptDraft) {
            return;
        }

        setIsPromptSaving(true);

        const result = await makeRequest(`/ai/admin/prompt-configs/${selectedPromptFunctionKey}`, 'post', {
            systemInstructionsText: selectedPromptDraft.systemInstructionsText || "",
            chunkInstructionsText: selectedPromptDraft.chunkInstructionsText || "",
            aggregateInstructionsText: selectedPromptDraft.aggregateInstructionsText || ""
        });

        if (result?.state === 'success' && result.data) {
            setPromptConfigs((currentConfigs) => currentConfigs.map((promptConfig) => (
                promptConfig.functionKey === selectedPromptFunctionKey ? result.data : promptConfig
            )));
            setPromptDrafts((currentDrafts) => ({
                ...currentDrafts,
                [selectedPromptFunctionKey]: mapPromptConfigToDraft(result.data)
            }));
            pushNotification({
                title: "Prompt AI aggiornato",
                message: "Le modifiche sono state salvate correttamente."
            });
        } else {
            pushNotification({
                severity: "error",
                title: "Salvataggio fallito",
                message: result?.message || "Non siamo riusciti a salvare il prompt AI."
            });
        }

        setIsPromptSaving(false);
    };

    const handleResetPromptConfig = async () => {
        if (!selectedPromptFunctionKey) {
            return;
        }

        setIsPromptSaving(true);

        const result = await makeRequest(`/ai/admin/prompt-configs/${selectedPromptFunctionKey}/reset`, 'post', {});

        if (result?.state === 'success' && result.data) {
            setPromptConfigs((currentConfigs) => currentConfigs.map((promptConfig) => (
                promptConfig.functionKey === selectedPromptFunctionKey ? result.data : promptConfig
            )));
            setPromptDrafts((currentDrafts) => ({
                ...currentDrafts,
                [selectedPromptFunctionKey]: mapPromptConfigToDraft(result.data)
            }));
            pushNotification({
                title: "Prompt AI ripristinato",
                message: "Sono stati ripristinati i valori di default."
            });
        } else {
            pushNotification({
                severity: "error",
                title: "Ripristino fallito",
                message: result?.message || "Non siamo riusciti a ripristinare il prompt AI."
            });
        }

        setIsPromptSaving(false);
    };

    const loadProviderConfig = async () => {
        setIsProviderLoading(true);
        setProviderErrorMessage("");

        const result = await makeRequest('/ai/admin/provider-config');

        if (result?.state === 'success' && result.data) {
            setProviderDraft({
                openAIApiKey: result.data.openAIApiKey || ""
            });
        } else {
            setProviderErrorMessage(result?.message || "Impossibile caricare la key AI.");
        }

        setIsProviderLoading(false);
    };

    const handleOpenProviderDialog = async () => {
        setIsProviderDialogOpen(true);
        setShowOpenAIApiKey(false);
        await loadProviderConfig();
    };

    const handleCloseProviderDialog = () => {
        if (isProviderSaving) {
            return;
        }

        setIsProviderDialogOpen(false);
        setShowOpenAIApiKey(false);
    };

    const handleSaveProviderConfig = async () => {
        setIsProviderSaving(true);

        const result = await makeRequest('/ai/admin/provider-config', 'post', {
            openAIApiKey: providerDraft.openAIApiKey || ""
        });

        if (result?.state === 'success' && result.data) {
            setProviderDraft({
                openAIApiKey: result.data.openAIApiKey || ""
            });
            pushNotification({
                title: "Key AI aggiornata",
                message: "La configurazione e` stata salvata correttamente."
            });
            setIsProviderDialogOpen(false);
            setShowOpenAIApiKey(false);
        } else {
            pushNotification({
                severity: "error",
                title: "Salvataggio fallito",
                message: result?.message || "Non siamo riusciti a salvare la key AI."
            });
        }

        setIsProviderSaving(false);
    };

    return (
        <>
            <Body
                mainElement={(
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="h4" gutterBottom>Dashboard</Typography>
                        </Box>

                        <Paper variant="outlined" sx={{ borderRadius: 3 }}>
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={2}
                                alignItems={{ xs: "flex-start", md: "center" }}
                                justifyContent="space-between"
                                sx={{ p: 2.5 }}
                            >
                                <Box>
                                    <Typography variant="h5">Prompt AI</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Gestisci i prompt separati per ogni funzione dell&apos;assistente AI.
                                    </Typography>
                                </Box>
                                <Button variant="contained" onClick={handleOpenPromptDialog}>
                                    Gestisci Prompt AI
                                </Button>
                            </Stack>
                        </Paper>

                        <Paper variant="outlined" sx={{ borderRadius: 3 }}>
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={2}
                                alignItems={{ xs: "flex-start", md: "center" }}
                                justifyContent="space-between"
                                sx={{ p: 2.5 }}
                            >
                                <Box>
                                    <Typography variant="h5">Key AI</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Gestisci la key OpenAI usata dall&apos;assistente AI. Se vuota, resta attivo il fallback su variabile ambiente.
                                    </Typography>
                                </Box>
                                <Button variant="contained" onClick={handleOpenProviderDialog}>
                                    Gestisci key
                                </Button>
                            </Stack>
                        </Paper>

                        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

                        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                            <Box sx={{ p: 2.5 }}>
                                <Typography variant="h5">Utenti</Typography>
                            </Box>
                            <Divider />
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Nome + Cognome</TableCell>
                                        <TableCell>Data Iscrizione</TableCell>
                                        <TableCell>Tipologia</TableCell>
                                        <TableCell>Min-Disp / Min-Iti</TableCell>
                                        <TableCell>Abbonamento</TableCell>
                                        <TableCell align="center">Flag</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user) => {
                                        const isSelected = selectedUserId === user.id;

                                        return (
                                            <TableRow
                                                key={user.id}
                                                hover
                                                selected={isSelected}
                                                onClick={() => handleSelectRow(user.id)}
                                                sx={{
                                                    cursor: "pointer",
                                                    '&.Mui-selected': {
                                                        backgroundColor: '#e8f1fb'
                                                    },
                                                    '&.Mui-selected:hover': {
                                                        backgroundColor: '#dcecff'
                                                    }
                                                }}
                                            >
                                                <TableCell>{user.id}</TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.5}>
                                                        <Typography fontWeight={600}>
                                                            {[user.name, user.lastname].filter(Boolean).join(" ") || "-"}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {user.email || "-"}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>{formatRegistrationDate(user.createdAt)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        color={user.userType === "admin" ? "warning" : "default"}
                                                        label={user.userType === "admin" ? "Admin" : "Basic"}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {`${formatMinutes(user.transcription?.availableSeconds)} / ${formatMinutes(user.transcription?.secondsUsed)}`}
                                                </TableCell>
                                                <TableCell>{formatSubscription(user.transcription?.packageId)}</TableCell>
                                                <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={() => handleSelectRow(user.id)}
                                                        inputProps={{ 'aria-label': `Seleziona utente ${user.id}` }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {!isLoading && users.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Nessun utente disponibile.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                </TableBody>
                            </Table>
                            <Divider />
                            <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    disabled={!selectedUser || isSaving}
                                    onClick={handleOpenEditDialog}
                                    sx={{
                                        minWidth: 180,
                                        '&.Mui-disabled': {
                                            backgroundColor: '#b7d3f2',
                                            color: '#ffffff'
                                        }
                                    }}
                                >
                                    Modifica
                                </Button>
                            </Box>
                        </Paper>
                    </Stack>
                )}
            />

            <AdminUserEditDialog
                open={isEditDialogOpen}
                user={selectedUser}
                saving={isSaving}
                onClose={handleCloseEditDialog}
                onSave={handleSaveUser}
            />

            <Dialog
                open={isProviderDialogOpen}
                onClose={handleCloseProviderDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Gestione Key AI</DialogTitle>
                <DialogContent dividers>
                    {isProviderLoading ? (
                        <Stack minHeight={180} alignItems="center" justifyContent="center">
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <Stack spacing={2} mt={1}>
                            {providerErrorMessage ? <Alert severity="error">{providerErrorMessage}</Alert> : null}

                            <Alert severity="info">
                                La key salvata qui viene usata dall&apos;assistenza AI. Se lasci il campo vuoto, il backend continuera` a usare `OPENAI_API_KEY` da ambiente.
                            </Alert>

                            <TextField
                                label="OpenAI API key"
                                fullWidth
                                type={showOpenAIApiKey ? "text" : "password"}
                                value={providerDraft.openAIApiKey}
                                onChange={(event) => setProviderDraft({ openAIApiKey: event.target.value })}
                                disabled={isProviderSaving}
                                helperText="Puoi inserire o modificare la key in qualsiasi momento."
                                InputProps={{
                                    endAdornment: (
                                        <Button
                                            onClick={() => setShowOpenAIApiKey((currentValue) => !currentValue)}
                                            disabled={isProviderSaving}
                                            sx={{ minWidth: 0, px: 1 }}
                                        >
                                            {showOpenAIApiKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                        </Button>
                                    )
                                }}
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseProviderDialog} disabled={isProviderSaving}>
                        Chiudi
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveProviderConfig}
                        disabled={isProviderLoading || isProviderSaving}
                    >
                        Salva key
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isPromptDialogOpen}
                onClose={handleClosePromptDialog}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>Gestione Prompt AI</DialogTitle>
                <DialogContent dividers>
                    {isPromptLoading ? (
                        <Stack minHeight={260} alignItems="center" justifyContent="center">
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <Stack spacing={3} mt={1}>
                            {promptErrorMessage ? <Alert severity="error">{promptErrorMessage}</Alert> : null}

                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={3}
                                alignItems="stretch"
                            >
                                <Stack spacing={2} sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0 }}>
                                    {groupedPromptConfigs.map((group) => (
                                        <Paper key={group.key} variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                                            <Box sx={{ px: 2, py: 1.5, backgroundColor: "#f7f9fc" }}>
                                                <Typography variant="subtitle1" fontWeight={700}>
                                                    {group.title}
                                                </Typography>
                                            </Box>
                                            <Divider />
                                            <Stack spacing={0.5} sx={{ p: 1 }}>
                                                {group.items.map((promptConfig) => {
                                                    const isSelected = promptConfig.functionKey === selectedPromptFunctionKey;

                                                    return (
                                                        <Button
                                                            key={promptConfig.functionKey}
                                                            variant={isSelected ? "contained" : "text"}
                                                            color={isSelected ? "primary" : "inherit"}
                                                            onClick={() => setSelectedPromptFunctionKey(promptConfig.functionKey)}
                                                            disabled={isPromptSaving}
                                                            sx={{
                                                                justifyContent: "flex-start",
                                                                textAlign: "left",
                                                                px: 1.5,
                                                                py: 1.25,
                                                                borderRadius: 2,
                                                                color: isSelected ? undefined : "text.primary",
                                                                backgroundColor: isSelected ? undefined : "transparent"
                                                            }}
                                                        >
                                                            {promptConfig.title}
                                                        </Button>
                                                    );
                                                })}
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>

                                {selectedPromptConfig ? (
                                    <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                                    <Alert severity="info">
                                        {selectedPromptConfig.description || "Prompt configurabile per la funzione selezionata."}
                                    </Alert>

                                    <TextField
                                        label={selectedPromptFunctionKey === 'transcript_summary' ? 'Prompt sintesi' : 'Istruzioni system'}
                                        multiline
                                        minRows={6}
                                        fullWidth
                                        value={selectedPromptDraft?.systemInstructionsText || ""}
                                        onChange={(event) => handlePromptDraftChange('systemInstructionsText', event.target.value)}
                                        helperText={selectedPromptFunctionKey === 'transcript_summary' ? 'Questo prompt viene inviato all’AI, seguito direttamente dal testo della trascrizione.' : "Queste istruzioni vengono aggiunte dopo orientamento psicologico e approcci integrativi dell'utente."}
                                        disabled={isPromptSaving}
                                    />

                                    {selectedPromptFunctionKey === 'transcript_summary' && (
                                        <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafcff' }}>
                                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                                Variabili del profilo psicologo
                                            </Typography>
                                            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                                                {SUMMARY_PROMPT_VARIABLES.map((variable) => (
                                                    <Chip key={variable.token} size="small" label={`${variable.token} · ${variable.label}`} />
                                                ))}
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                Inserisci una variabile con le parentesi graffe. Per aggiungere del testo solo quando un valore è presente usa, ad esempio: <code>{'[approccio ? "e approccio {approccio}"]'}</code>.
                                            </Typography>
                                        </Paper>
                                    )}

                                    {selectedPromptFunctionKey !== 'transcript_summary' && <TextField
                                        label="Istruzioni analisi chunk"
                                        multiline
                                        minRows={8}
                                        fullWidth
                                        value={selectedPromptDraft?.chunkInstructionsText || ""}
                                        onChange={(event) => handlePromptDraftChange('chunkInstructionsText', event.target.value)}
                                        helperText="Una riga per istruzione. Definisce il comportamento sui singoli chunk di testo."
                                        disabled={isPromptSaving}
                                    />}

                                    {selectedPromptFunctionKey !== 'transcript_summary' && <TextField
                                        label="Istruzioni aggregazione finale"
                                        multiline
                                        minRows={8}
                                        fullWidth
                                        value={selectedPromptDraft?.aggregateInstructionsText || ""}
                                        onChange={(event) => handlePromptDraftChange('aggregateInstructionsText', event.target.value)}
                                        helperText="Una riga per istruzione. Definisce come aggregare i risultati finali dei chunk."
                                        disabled={isPromptSaving}
                                    />}
                                    </Stack>
                                ) : null}
                            </Stack>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePromptDialog} disabled={isPromptSaving}>
                        Chiudi
                    </Button>
                    <Button
                        color="warning"
                        onClick={handleResetPromptConfig}
                        disabled={!selectedPromptFunctionKey || isPromptLoading || isPromptSaving}
                    >
                        Ripristina default
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSavePromptConfig}
                        disabled={!selectedPromptFunctionKey || isPromptLoading || isPromptSaving}
                    >
                        Salva prompt
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AdminDashboardPage;
